import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Tooltip } from 'antd';
import { useAIToken } from '../../aiServices/aiTokenService';
import { performAISearchAndFetch } from '../../containers/SearchPage/SearchPage.duck';
import { incrementSuggestion } from '../../ducks/customAISearch.duck';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useConfiguration } from '../../context/configurationContext';
import AIAnalysisModal from '../AIAnalysisModal';
import goLogoWhite from '../../containers/EarningsCalculatorPage/CalculatorPageComponents/CalculatorPageHeroSection/img/goWhiteSmall.png';
import css from './CustomAISearch.module.css';

const PRESET_SUGGESTIONS = [
  { id: 1, text: 'Family friendly', count: 0 },
  { id: 2, text: 'Perfect for couples', count: 0 },
  { id: 3, text: 'Small groups', count: 0 },
  { id: 4, text: 'Vehicles near me', count: 0 },
  { id: 5, text: 'Pet friendly', count: 0 },
  { id: 6, text: '6 seatbelts', count: 0 },
  { id: 7, text: 'Under £175 per night', count: 0 },
  { id: 8, text: 'Unlimited mileage', count: 0 },
  { id: 9, text: 'Drive on a normal car licence', count: 0 },
  { id: 10, text: 'Festival ready', count: 0 },
  { id: 11, text: 'Adventure ready', count: 0 },
];

const CustomAISearch = ({ suggestions: suggestionsProp, showErrorBanner = true }) => {
  const [searchValue, setSearchValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);

  const searchRef = useRef(null);
  const recognitionRef = useRef(null);

  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();

  // Token service - only check loading state
  const { loading: isTokenLoading } = useAIToken();

  // Redux state - AI Search
  const aiSearchInProgress = useSelector(state => state.SearchPage?.aiSearchInProgress || false);
  const aiSearchError = useSelector(state => state.SearchPage?.aiSearchError);

  // Use suggestions from props (passed from parent), fallback to PRESET_SUGGESTIONS
  const suggestions = suggestionsProp || PRESET_SUGGESTIONS;

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Speech recognition setup
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-GB';

      recognitionRef.current.onresult = (event) => {
        const resultIndex = event.resultIndex;
        const transcript = event.results[resultIndex][0].transcript;
        if (event.results[resultIndex].isFinal) {
          setSearchValue(prev => prev + transcript);
        }
      };

      recognitionRef.current.onend = () => setIsListening(false);
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const handleFocus = () => setShowSuggestions(true);

  const handleSuggestionClick = (suggestion) => {
    setSearchValue(suggestion.text);
    setShowSuggestions(false);

    // Dispatch Redux action - handles optimistic update and MongoDB persistence
    dispatch(incrementSuggestion(suggestion.id));
  };

  const handleMicClick = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please use Chrome or Edge.');
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
    } else {
      setSearchValue('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const handleSearch = async () => {
    if (!searchValue.trim()) return;

    setShowSuggestions(false);
    setShowAnalysisModal(true);

    try {
      // Dispatch thunk - it populates Redux state
      await dispatch(performAISearchAndFetch(searchValue.trim(), config));

      // Navigate to SearchPage WITHOUT query parameters
      // Redux state will drive the UI
      history.push(
        createResourceLocatorString(
          'SearchPage',
          routeConfiguration,
          {},
          {} // CHANGED: Empty query params
        )
      );

      // Close modal after navigation
      setShowAnalysisModal(false);
    } catch (error) {
      // Error already in Redux state
      console.error('AI search error:', error);
      // Close modal on error
      setShowAnalysisModal(false);
    }
  };

  const isSearching = aiSearchInProgress;
  const isButtonDisabled = isSearching || isTokenLoading || !searchValue.trim();
  const buttonText = isSearching ? 'Searching...' : 'Search';

  return (
    <>
      <AIAnalysisModal
        isOpen={showAnalysisModal}
        message="GoPilot™ is on it..."
        onClose={() => setShowAnalysisModal(false)}
        showProgress={false}
      />

      <div ref={searchRef} className={css.searchContainer}>
        <div className={css.searchBar}>
        <div className={css.inputWrapper}>
          {/* <div className={css.aiBadge}>
            <img src={goLogoWhite} alt="Go" className={css.goLogo} />
            <span className={css.aiBadgeText}>AI</span>
          </div> */}

          <Tooltip
            title="Tell GoPilot™ what you're searching for..."
            color="#303b4b"
          >
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onFocus={handleFocus}
              onKeyPress={(e) => e.key === 'Enter' && !isButtonDisabled && handleSearch()}
              placeholder="Ask GoPilot™"
              className={css.searchInput}
              disabled={isSearching}
            />
          </Tooltip>

          <button
            onClick={handleSearch}
            className={`${css.searchButton} ${isSearching ? css.searching : ''}`}
            disabled={isButtonDisabled}
            title="Search"
          >
            {isSearching ? (
              <svg className={css.spinner} viewBox="0 0 24 24">
                <circle className={css.spinnerCircle} cx="12" cy="12" r="10" />
              </svg>
            ) : (
              <svg className={css.searchIcon} viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            )}
          </button>
        </div>

        <button
          onClick={handleMicClick}
          className={`${css.micButton} ${isListening ? css.listening : ''}`}
          disabled={isSearching}
        >
          {isListening && (
            <>
              <div className={css.listeningRipple}></div>
              <div className={css.listeningRipple} style={{ animationDelay: '0.5s' }}></div>
              <div className={css.listeningRipple} style={{ animationDelay: '1s' }}></div>
            </>
          )}
          <svg className={`${css.micIcon} ${isListening ? css.listening : ''}`} viewBox="0 0 24 24">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
          {isListening && <span className={css.listeningText}>Listening...</span>}
        </button>
      </div>

      {aiSearchError && !isSearching && showErrorBanner && (
        <div className={css.errorMessage}>
          No results found. Try a different search.
        </div>
      )}

      {showSuggestions && !isSearching && (
        <div className={css.suggestionsDropdown}>
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.id}
              onClick={() => handleSuggestionClick(suggestion)}
              className={css.suggestionItem}
            >
              {suggestion.text}
            </div>
          ))}
        </div>
      )}
      </div>
    </>
  );
};

export default CustomAISearch;
